import { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { FloatingInput, Spinner } from "../../Components/Common";
import { getUsers, updateUser } from "../../Services/userService";
import { VALIDATION_MESSAGES } from "./constant";

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required(VALIDATION_MESSAGES.FIRST_NAME_REQUIRED),
  last_name: Yup.string().required(VALIDATION_MESSAGES.LAST_NAME_REQUIRED),
  mobile: Yup.string()
    .matches(/^\d{10}$/, VALIDATION_MESSAGES.MOBILE_VALIDATION_MESSAGE)
    .required(VALIDATION_MESSAGES.MOBILE_NUMBER_REQUIRED),
  email: Yup.string()
    .email(VALIDATION_MESSAGES.INVALID_MAIL)
    .required(VALIDATION_MESSAGES.EMAIL_REQUIRED),
});

const Profile = () => {
  const [userInitialValues, setUserInitialValues] = useState({
    first_name: "",
    last_name: "",
    role: "",
    email: "",
    mobile: "",
    company_name: "",
  });
  const [userApiLoading, setUserApiLoading] = useState(false);

  const fetchUserDetails = async () => {
    try {
      setUserApiLoading(true);
      const response = await getUsers();
      if (response.status === 200) {
        setUserInitialValues({
          first_name: response.data?.first_name,
          last_name: response.data?.last_name,
          role: response.data?.role,
          email: response.data?.email,
          mobile: response.data?.mobile,
          company_name: response.data?.company?.name,
        });
      }
    } catch {
      toast.error(VALIDATION_MESSAGES.FAILED_FETCH_USERS);
    } finally {
      setUserApiLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const form = new FormData();
      form.append("first_name", values.first_name || "");
      form.append("last_name", values.last_name || "");
      form.append("email", values.email || "");

      const response = await updateUser(form, true);
      if (response.status === 200 || response.status === 204) {
        toast.success(VALIDATION_MESSAGES.USER_UPDATED);
      } else {
        toast.error(VALIDATION_MESSAGES.FAILED_UPDATE_USER);
      }
    } catch (error) {
      toast.error(VALIDATION_MESSAGES.FAILED_UPDATE_USER);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  return (
    <div className="p-4">
      <Formik
        initialValues={userInitialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, dirty }) => (
          <Form className="relative max-w-xl p-6 mx-auto space-y-6">
            <h2 className="mb-6 text-3xl font-bold text-center text-primary">
              Profile
            </h2>

            {userApiLoading ? (
              <div className="flex justify-center py-10">
                <Spinner size={4} />
              </div>
            ) : (
              <div className="space-y-3">
                {/* First & Last Name */}
                <div className="flex flex-col gap-3 md:flex-row">
                  <FloatingInput
                    name="first_name"
                    label="First Name *"
                    css="w-full"
                  />
                  <FloatingInput
                    name="last_name"
                    label="Last Name *"
                    css="w-full"
                  />
                </div>

                {/* Email & Mobile */}
                <div className="flex flex-col gap-3 md:flex-row">
                  <FloatingInput
                    name="email"
                    label="Email *"
                    type="email"
                    css="w-full"
                  />
                  <FloatingInput
                    name="mobile"
                    label="Mobile No. *"
                    type="tel"
                    disabled
                    css="w-full"
                  />
                </div>

                {/* Role & Company */}
                <div className="flex flex-col gap-3 md:flex-row">
                  <FloatingInput
                    name="role"
                    label="Role *"
                    disabled
                    css="w-full"
                  />
                  <FloatingInput
                    name="company_name"
                    label="Company Name *"
                    disabled
                    css="w-full"
                  />
                </div>

                {/* Submit button */}
                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={!dirty || isSubmitting}
                    className={`font-semibold py-2 px-6 rounded ${
                      !dirty || isSubmitting
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "bg-primary hover:bg-secondary text-white"
                    }`}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Profile;
